import Grid from "components/GridView/components/Grid";
import { MouseEvent, useMemo, useRef, useState } from "react";
import { GridSchema } from "components/GridView/GridView.types";
import { useTranslation } from "react-i18next";
import IconButton from "components/blocks/IconButton";
import { useQuery } from "hooks/useQuery";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ExaminerSelectedTemplateType } from "..";
import { ViewRefereeEvaluationModalProps } from "./ViewRefereeEvaluationModal";
import AcceptRejectRefereeEvaluationDDL from "./AcceptRejectRefereeEvaluationDDL";
import { useCookieContext } from "contexts";
import {  AccreditationTypes } from "services/examinersService/Model/ExaminerTypes";
import {RefereesService} from "../../../../../../../../../services/RefereeService";
import {RefereeListResult} from "../../../../../../../../../services/RefereeService/Model/RefereeTypes";
import ReferRefereeModal from "../ReferRefereesModal";
import {DeleteRefereeModal} from "../DeleteRefereeModal";
import ViewRefereeEvaluationModal from "./ViewRefereeEvaluationModal";
import AcceptRefereeRewardModal, {AcceptRefereeRewardModalProps} from "./AcceptRefereeRewardModal";
import {DeleteRefereeModalProps} from "./DeleteRefereeModal";
import AddRefereeModal from "../AddRefreeModal";
import GenericActionsModal from "../../../../../OrderTaskActionModals/GenericActionsModal";
import {AddIterationsRefereeModal} from "../AddItrationsRefereeModal";


export const EXAMINER_TASK_STATUS = {
  Pending: {
    key: "examinerTaskPending",
    value: "Pending",
  },
  Cancel: {
    key: "examinerTaskCancel",
    value: "Cancel",
  },
  Apologies: {
    key: "examinerTaskApologies",
    value: "Apologies",
  },
  Returned: {
    key: "examinerTaskReturned",
    value: "Returned",
  },
  Submitted: {
    key: "examinerTaskSubmitted",
    value: "Submitted",
  },
  Evaluated: {
    key: "examinerTaskEvaluated",
    value: "Evaluated",
  },
  Accepted: {
    key: "examinerTaskAccepted",
    value: "Accepted",
  },
};
type ExaminerTaskStatus = keyof typeof EXAMINER_TASK_STATUS;

const RefereesTable = ({
  orderId,
  courseOrderEvaluationId,
  selectedTemplate,
  setSelectedTemplate 
}: {
  orderId: number;
  courseOrderEvaluationId: number;
  selectedTemplate: ExaminerSelectedTemplateType;
  setSelectedTemplate :any
}) => {
  const { isAr } = useCookieContext();
  const { t } = useTranslation(["Orders"]);

  const examinersEvaluationStatusRef = useRef<{
    [key: string]: { examinerId: number; shouldShowAcceptRejectEvaluationDropdown: boolean }[];
  }>({});
  const [triggerTableUpdate, setTriggerTableUpdate] = useState(false);
  const [checkedExaminerToReferer, setCheckedExaminerToReferer] = useState<RefereeListResult[]>([]);
  const [isAddExaminerModalOpened, setIsAddExaminerModalOpened] = useState(false);
  const [isAddIterationModalOpened, setIsAddIterationModalOpened] = useState(false);
  const [refererExaminersModalProps, setRefererExaminersModalProps] = useState<{
    isOpen: boolean;
    alreadyReferredExaminerId: number | undefined;
  }>({
    isOpen: false,
    alreadyReferredExaminerId: undefined,
  });
  const [viewExaminerEvaluationModalProps, setViewExaminerEvaluationModalProps] =
    useState<ViewRefereeEvaluationModalProps | null>(null);
  const [deleteExaminerModalProps, setDeleteExaminerModalProps] = useState<
    DeleteRefereeModalProps | null | undefined
  >();
  const [AcceptExaminerRewardModalProps, setAcceptExaminerRewardModalProps] = useState<
    AcceptRefereeRewardModalProps | null | undefined
  >();

  const { data: selectedExaminers } = useQuery({
    queryFn: async () => {
      const data = await RefereesService.getAllRefereesByCourseOrderId(orderId);

      debugger;
      //! Order by examinerName and then by id
      data.data.sort((a, b) => a.refereeName.localeCompare(b.refereeName) || a.id - b.id);

      data.data.forEach((examiner) => {
        if (!examinersEvaluationStatusRef.current[examiner.refereeName]) {
          examinersEvaluationStatusRef.current[examiner.refereeName] = [];
        }

        if (!!examiner.status) {
          examinersEvaluationStatusRef.current[examiner.refereeName].forEach((examinerEvaluation) => {
            examinerEvaluation.shouldShowAcceptRejectEvaluationDropdown = false;
          });
        }

        examinersEvaluationStatusRef.current[examiner.refereeName].push({
          examinerId: examiner.id,
          shouldShowAcceptRejectEvaluationDropdown: !!examiner.status,
        });
      });

      return data;
    },
    triggers: [orderId, triggerTableUpdate],
    options: {
      enabled: !!orderId,
    },
  });

  const onCheckExaminer = (e: MouseEvent<HTMLInputElement, globalThis.MouseEvent>, userId: string | number) => {
    if (e.currentTarget.checked) {
      setCheckedExaminerToReferer((old) => [...old, selectedExaminers!.find((examiner) => examiner.id === userId)!]);
    } else {
      setCheckedExaminerToReferer((old) => old.filter((examiner) => examiner.id !== userId));
    }
  };

  const onSelectAllNewExaminer = (e: MouseEvent<HTMLInputElement, globalThis.MouseEvent>) => {
    if (e.currentTarget.checked) {
      setCheckedExaminerToReferer(selectedExaminers?.filter((examiner) => !examiner.status) ?? []);
    } else {
      setCheckedExaminerToReferer([]);
    }
  };

  const onDeleteExaminer = (row: RefereeListResult) => {
    setDeleteExaminerModalProps({
      isOpen: true,
      onClose: (shouldUpdateExaminersTable: boolean) => {
        setDeleteExaminerModalProps(null);
        if (shouldUpdateExaminersTable) {
          setTriggerTableUpdate(!triggerTableUpdate);
        }
      },
      selectedExaminerId: row.id,
      examinerStatus: row.status,
      examinerName: row.refereeName,
    });
  };

  const onViewExaminerEvaluation = (row: RefereeListResult) => {
    setViewExaminerEvaluationModalProps({
      evaluationPercentage: row.evaluationPercentage,
      onClose: () => {
        setViewExaminerEvaluationModalProps(null);
      },
      refereeEvaluationFormValue: row.refereeEvaluationFormValue,
      selectedTemplate: selectedTemplate,
      formDefinition: row.evaluationForm.jsonSchema,
    });
  };

  const onAcceptExaminerReward = (row: RefereeListResult) => {
    setAcceptExaminerRewardModalProps({
      isOpen: true,
      onClose: (shouldUpdateTable: boolean) => {
        setAcceptExaminerRewardModalProps(null);
        if (shouldUpdateTable) {
          setTriggerTableUpdate(!triggerTableUpdate);
        }
      },
      courseOrderExaminerId: row.id,
    });
  };

  const gridSchema = useMemo<GridSchema[]>(
    () => [
      // {
      //   accessor: "",
      //   field: "",
      //   displayName: "",
      //   showOnMobile: true,
      //   render: (row: RefereeListResult) => {
      //     return (
      //       <input
      //         type="checkbox"
      //         disabled={
      //           row.status === EXAMINER_TASK_STATUS.Pending.value || row.status === EXAMINER_TASK_STATUS.Accepted.value
      //         }
      //         checked={checkedExaminerToReferer.some((examiner) => examiner.id === row.id)}
      //         onClick={(e) => {
      //           if (
      //             !(
      //               row.status === EXAMINER_TASK_STATUS.Submitted.value ||
      //               row.status === EXAMINER_TASK_STATUS.Evaluated.value ||
      //               row.status === EXAMINER_TASK_STATUS.Apologies.value ||
      //               row.status === EXAMINER_TASK_STATUS.Cancel.value
      //             )
      //           ) {
      //             onCheckExaminer(e, row.id);
      //           } else {
      //             setRefererExaminersModalProps({
      //               isOpen: true,
      //               alreadyReferredExaminerId: row.id,
      //             });
      //           }
      //         }}
      //       />
      //     );
      //   },
      //   renderHeader: () => {
      //     return <input type="checkbox" onClick={onSelectAllNewExaminer} />;
      //   },
      // },
      {
        field: "refereeName",
        displayName: t("refereeName"),
        showOnMobile: true,
        render: (row: RefereeListResult) => {
          return (
            <span className={row.status === EXAMINER_TASK_STATUS.Cancel.value ? "text-overline" : ""}>
              {row.refereeName}
            </span>
          );
        },
      },
      {
        field: "round",
        displayName: t("round"),
        render: (row: RefereeListResult) => {
          const isDelayed =
            row.deadLine && row.status !== EXAMINER_TASK_STATUS.Submitted.value
              ? new Date().getTime() > new Date(row.deadLine).getTime()
              : false;

          return (
            <div className="d-flex justify-content-between">
              <span>{row.iteration}</span>
              {row.status === EXAMINER_TASK_STATUS.Pending.value && row.isSentInvitation && (
                <OverlayTrigger placement="top" overlay={<Tooltip>{t("notRegisteredWithSentInvitation")}</Tooltip>}>
                  <i className="icon-help text-primary"></i>
                </OverlayTrigger>
              )}
              {isDelayed && <i className="icon-warning text-danger"></i>}
            </div>
          );
        },
      },
      {
        field: "evaluationPercentage",
        displayName: t("evaluation"),
        render: (row: RefereeListResult) => {
          return <>{row.evaluationPercentage ? `${row.evaluationPercentage.toFixed(2)}%` : "-"}</>;
        },
      },
      {
        field: "recommendations",
        displayName: t("recommendations"),
        render: (row) => {
          return row.recommendations;
        },
      },
      {
        field: "taskStatus",
        displayName: t("taskStatus"),
        render: (row) => {
          return row.status ? t(EXAMINER_TASK_STATUS[row.status as ExaminerTaskStatus].key) : "-";
        },
      },
      {
        field: "confirmRefereesResult",
        displayName: t("confirmRefereesResult"),
        render: (row) => {
          return row.status === EXAMINER_TASK_STATUS.Submitted.value &&
            examinersEvaluationStatusRef.current[row.refereeName].some(
              (examinerEvaluation) =>
                examinerEvaluation.shouldShowAcceptRejectEvaluationDropdown && examinerEvaluation.examinerId === row.id,
            ) ? (
            <AcceptRejectRefereeEvaluationDDL row={row} setTriggerTableUpdate={setTriggerTableUpdate} />
          ) : (
            "-"
          );
        },
      },
      {
        field: "actions",
        displayName: t("actions"),
        render: (row: RefereeListResult) => {
          const isDeleteIconShown =
            row.status !== EXAMINER_TASK_STATUS.Submitted.value &&
            row.status !== EXAMINER_TASK_STATUS.Cancel.value &&
            row.status !== EXAMINER_TASK_STATUS.Apologies.value;

          const isRewardIconShown =
            row.status === EXAMINER_TASK_STATUS.Submitted.value &&
            examinersEvaluationStatusRef.current[row.refereeName].some(
              (examinerEvaluation) =>
                examinerEvaluation.shouldShowAcceptRejectEvaluationDropdown && examinerEvaluation.examinerId === row.id,
            );

          const isViewEvaluationIconShown =
            row.status === EXAMINER_TASK_STATUS.Submitted.value || row.status === EXAMINER_TASK_STATUS.Evaluated.value;

          return (
            <div className="d-flex gap-2">
              {isViewEvaluationIconShown && (
                <IconButton
                  icon="icon-view"
                  fitToIconWidth
                  innerIconColor="primary"
                  size="md"
                  onClick={() => {
                    onViewExaminerEvaluation(row);
                  }}
                />
              )}
              {isDeleteIconShown && (
                <IconButton
                  icon="icon-delete"
                  fitToIconWidth
                  innerIconColor="danger"
                  size="md"
                  onClick={() => onDeleteExaminer(row)}
                />
              )}

              {isRewardIconShown && (
                <IconButton
                  icon={row.isConfirmFees ? "icon-reward-accepted" : "icon-reward"}
                  innerIconColor={row.isConfirmFees ? "success" : "dark"}
                  fitToIconWidth
                  size="md"
                  title={row.isConfirmFees ? t("reward-accepted") : t("reward-ac")}
                  onClick={
                    !row.isConfirmFees ||  row.accreditationType === AccreditationTypes.Reject.label
                      ? () => onAcceptExaminerReward(row)
                      : () => {}
                  }
                />
              )}
            </div>
          );
        },
      },
    ],
    [checkedExaminerToReferer, selectedExaminers, isAr],
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <h4>{t("evaluations")}</h4>
        <div>
          {/*<button*/}
          {/*    className="btn btn-outline-secondary p-10-32P rounded-4 mx-2"*/}
          {/*    disabled={!selectedTemplate.id}*/}
          {/*    onClick={() => {*/}
          {/*      setIsAddIterationModalOpened(true);*/}
          {/*    }}*/}
          {/*>*/}
          {/*  {t("AddIteration")} ({selectedTemplate.currentIteration})*/}
          {/*</button>*/}
          
          <button
            className="btn btn-primary p-10-32P rounded-4"
            disabled={!selectedTemplate.id}
            onClick={() => {
              setIsAddExaminerModalOpened(true);
            }}
          >
            {t("addReferees")}
          </button>

       
          
          {/*{!!checkedExaminerToReferer?.length && (*/}
          {/*  <button*/}
          {/*    className="btn border me-2 p-10-32P rounded-4 text-dark"*/}
          {/*    onClick={() => {*/}
          {/*      setRefererExaminersModalProps({*/}
          {/*        isOpen: true,*/}
          {/*        alreadyReferredExaminerId: undefined,*/}
          {/*      });*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    {t("referReferees")}*/}
          {/*  </button>*/}
          {/*)}*/}
        </div>
      </div>

      {!!selectedExaminers?.length && <Grid data={selectedExaminers} gridSchema={gridSchema} />}


      
      
      <AddRefereeModal
        isOpen={isAddExaminerModalOpened}
        courseOrderEvaluationId={courseOrderEvaluationId}
        onClose={(shouldUpdateTable?: boolean) => {
          setIsAddExaminerModalOpened(false);
          if (shouldUpdateTable) {
            setTriggerTableUpdate(!triggerTableUpdate);
          }
        }}
      />

      <ReferRefereeModal
        isOpen={refererExaminersModalProps.isOpen}
        alreadyReferredExaminerId={refererExaminersModalProps.alreadyReferredExaminerId}
        checkedExaminerToReferer={checkedExaminerToReferer}
        onClose={() => {
          setRefererExaminersModalProps({
            isOpen: false,
            alreadyReferredExaminerId: undefined,
          });
          setCheckedExaminerToReferer([]);
          setTriggerTableUpdate(!triggerTableUpdate);
        }}
      />

      <AddIterationsRefereeModal props={
        {
        isOpen:isAddIterationModalOpened,
        onClose:(shouldUpdateTable?: boolean) => {
          setIsAddIterationModalOpened(false);
          if (shouldUpdateTable && setSelectedTemplate) {
            setSelectedTemplate({ ...selectedTemplate , currentIteration: selectedTemplate.currentIteration? selectedTemplate.currentIteration + 1 :1});
            setTriggerTableUpdate(!triggerTableUpdate);
          }
        },
        selectedCourseOrderEvaluationId:courseOrderEvaluationId
      }
      }/>
      <DeleteRefereeModal props={deleteExaminerModalProps} />
      <ViewRefereeEvaluationModal props={viewExaminerEvaluationModalProps} />
      <AcceptRefereeRewardModal
        isOpen={!!AcceptExaminerRewardModalProps?.isOpen}
        onClose={AcceptExaminerRewardModalProps?.onClose}
        courseOrderExaminerId={AcceptExaminerRewardModalProps?.courseOrderExaminerId}
      />
    </>
  );
};

export default RefereesTable;
